import { Info } from '@mui/icons-material'
import { Box, Button, Tooltip, Typography } from '@mui/material'
import {
  fireJourneyReallocated,
  fireQuoteSelected,
} from 'analytics/bookingEvents'
import handleDestinationSupplier from 'components/Bookings/Common/DestinationSupplierCheck'
import CallButton from 'components/UI/CallButton'
import useJob from 'hooks/useJob'
import { useEffect, useRef } from 'react'

type QuoteEntryProps = {
  supplierName: string
  contactNumber: string | undefined
  price: string
  eta: number
  bookingVehicleId: string
  supplierQuoteReference: string
  quoteReference: string
  isDestinationSupplier: boolean
  setIsReallocateModalOpen: (x: boolean) => void
  vehicleTypeName?: string
  isPreferredSupplier?: boolean
  currency?: string
  previousPrice?: number
}

const QuoteEntry = ({
  supplierName,
  contactNumber,
  price,
  eta,
  bookingVehicleId,
  supplierQuoteReference,
  quoteReference,
  isDestinationSupplier,
  setIsReallocateModalOpen,
  vehicleTypeName,
  isPreferredSupplier,
  currency,
  previousPrice,
}: QuoteEntryProps) => {
  const { bookingData, isLoading, reallocateBooking } = useJob()
  const reallocateFiredRef = useRef(false)

  useEffect(() => {
    if (bookingData?.status === 204 || bookingData?.status === 200) {
      if (!reallocateFiredRef.current) {
        reallocateFiredRef.current = true
        fireJourneyReallocated({
          bookingVehicleId,
          quoteGroupId: quoteReference,
          toSupplierQuoteReference: supplierQuoteReference,
          vehicleType: vehicleTypeName,
          price: Number.parseFloat(price as unknown as string),
          previousPrice,
          currency,
          isPreferredSupplier,
        })
      }
      setIsReallocateModalOpen(false)
    }
  }, [bookingData])

  const handleBooking = () => {
    fireQuoteSelected({
      surface: 'reallocate',
      quoteGroupId: quoteReference,
      selectedQuote: {
        supplierQuoteReference,
        vehicleTypeName,
        price: Number.parseFloat(price as unknown as string),
        estimatedPriceCurrency: currency,
        eta,
        isPreferredSupplier,
        isDestinationLogicQuote: isDestinationSupplier,
      },
    })
    const data = {
      quoteReference,
      supplierQuoteReference,
    }
    reallocateBooking(bookingVehicleId, data)
  }

  const etaMins = Math.floor(eta / 60) || 0
  let etaColor = '#70C86D'
  if (etaMins > 14 && etaMins < 20) {
    etaColor = '#FBB95F'
  }
  if (etaMins >= 20) {
    etaColor = '#E55448'
  }

  const handleIsDestinationSupplier = () => {
    if (isDestinationSupplier) {
      handleDestinationSupplier(() => handleBooking())
    } else {
      handleBooking()
    }
  }

  return (
    <Box
      component="div"
      sx={{
        width: '100%',
        backgroundColor: '#F0F0F0',
        padding: '10px',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderTop: '1px solid #DADCDF',
      }}
    >
      <Typography component="p" sx={{ color: '#A1A1A1', width: '185px' }}>
        {supplierName}

        {isDestinationSupplier && (
          <Tooltip title="Destination Supplier" arrow placement="top-end">
            <Info
              fontSize="medium"
              sx={(theme) => ({
                color: theme.palette.info.dark,
                paddingLeft: '5px',
                verticalAlign: 'bottom',
              })}
            />
          </Tooltip>
        )}
      </Typography>
      <Typography component="p" sx={{ color: '#A1A1A1' }}></Typography>
      <CallButton phone={contactNumber} label={`Call`} />
      <Typography
        component="p"
        sx={{
          color: 'black',
          fontSize: '18px',
          fontWeight: 'bold',
          width: '100px',
          textAlign: 'right',
        }}
      >
        £{parseFloat(price).toFixed(2)}
      </Typography>
      <Typography
        component="p"
        sx={{
          color: etaColor,
          fontSize: '16px',
          textAlign: 'left',
          width: '120px',
        }}
      >
        ETA: {Math.floor(eta)} min(s)
      </Typography>
      <Button
        sx={{ backgroundColor: '#B4B4B4' }}
        disabled={isLoading}
        onClick={handleIsDestinationSupplier}
      >
        ALLOCATE
      </Button>
    </Box>
  )
}
export default QuoteEntry
